import { useState, useEffect, useMemo } from "react";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Inbox, GripVertical, AlertTriangle, UserPlus, Clock } from "lucide-react";
import { toDate, fmtDate } from "@/lib/calendarUtils";
import { StaffingBadge } from "@/components/StaffingBadge";

const URGENCY_STYLE = {
  today: "bg-red-600 text-white",
  h24: "bg-amber-500 text-white",
  h48: "bg-yellow-400 text-slate-900",
};
const WARN_CHIP = {
  double_book: "bg-red-100 text-red-700",
  leave: "bg-red-100 text-red-700",
  travel: "bg-amber-100 text-amber-700",
  availability: "bg-amber-100 text-amber-700",
};
const FILTERS = ["all", "today", "h24", "h48"];

export default function UnassignedTray({ items, warnedIds, warnTypes = {}, groupByWorker, view, onDragStart, onDragEnd }) {
  const { t } = useI18n();
  const canDrag = groupByWorker && (view === "day" || view === "week");
  const [now, setNow] = useState(Date.now());
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  const enriched = useMemo(() => {
    const todayStr = fmtDate(new Date(now));
    return items.map((it) => {
      const startMs = toDate(it.date, it.startTime).getTime();
      const durH = Math.max(0, (toDate(it.date, it.endTime).getTime() - startMs) / 3600000);
      const hrs = (startMs - now) / 3600000;
      let urgency = null;
      if (it.date === todayStr) urgency = "today";
      else if (hrs >= 0 && hrs <= 24) urgency = "h24";
      else if (hrs > 24 && hrs <= 48) urgency = "h48";
      return { ...it, startMs, durH: Math.round(durH * 10) / 10, urgency };
    }).sort((a, b) => a.startMs - b.startMs);
  }, [items, now]);

  const counts = useMemo(() => ({
    all: enriched.length,
    today: enriched.filter((e) => e.urgency === "today").length,
    h24: enriched.filter((e) => e.urgency === "h24").length,
    h48: enriched.filter((e) => e.urgency === "h48").length,
  }), [enriched]);

  const shown = filter === "all" ? enriched : enriched.filter((e) => e.urgency === filter);

  return (
    <Card className="border-slate-200 p-4" data-testid="unassigned-tray">
      <div className="flex items-center gap-2 mb-1">
        <Inbox className="h-4 w-4 text-brand" />
        <h3 className="font-semibold text-slate-900 text-sm">{t("tray.title", "Unassigned")}</h3>
        {counts.all > 0 && <Badge className="bg-brand">{counts.all}</Badge>}
        {counts.today > 0 && <Badge className="bg-red-600" data-testid="tray-today-count">{counts.today} {t("urg.today", "Today")}</Badge>}
      </div>
      <p className="text-[11px] text-slate-400 mb-2">
        {canDrag ? t("tray.subtitle", "Drag onto a worker column to assign.") : t("tray.dropHint", "Switch to By-worker Day or Week view to assign by dragging.")}
      </p>

      <div className="flex gap-1 mb-3" data-testid="tray-urgency-filter">
        {FILTERS.map((f) => (
          <button key={f} data-testid={`tray-filter-${f}`} onClick={() => setFilter(f)}
            className={`px-2 py-1 rounded-md text-[11px] font-semibold transition ${filter === f ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500 hover:text-slate-800"}`}>
            {t(`urg.${f}`, { all: "All", today: "Today", h24: "Next 24h", h48: "Next 48h" }[f])} {counts[f] > 0 && `(${counts[f]})`}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <p className="text-sm text-slate-500">{t("tray.none", "Nothing unassigned.")}</p>
      ) : (
        <div className="space-y-2 max-h-[380px] overflow-y-auto">
          {shown.map((it) => {
            const warned = warnedIds.has(it.id);
            const types = warnTypes[it.id] || [];
            return (
              <div key={it.id} data-testid={`tray-item-${it.id}`}
                draggable={canDrag}
                onDragStart={() => onDragStart(it)}
                onDragEnd={onDragEnd}
                className={`rounded-lg border p-2.5 text-xs ${canDrag ? "cursor-grab active:cursor-grabbing hover:border-brand hover:shadow-sm" : "opacity-90"} ${it.urgency === "today" ? "border-red-300 bg-red-50" : warned ? "border-red-200 bg-red-50/60" : "border-slate-200 bg-white"}`}>
                <div className="flex items-start gap-2">
                  {canDrag && <GripVertical className="h-4 w-4 text-slate-300 shrink-0 mt-0.5" />}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Badge className={it.kind === "job" ? "bg-brand" : "bg-teal-600"}>
                        {it.kind === "job" ? t("cal.job", "Job") : t("cal.shift", "Shift")}
                      </Badge>
                      {it.urgency && (
                        <Badge data-testid={`tray-urgency-${it.id}`} className={URGENCY_STYLE[it.urgency]}>
                          <Clock className="h-3 w-3 mr-1" />{t(`urg.${it.urgency}`, { today: "Today", h24: "Next 24h", h48: "Next 48h" }[it.urgency])}
                        </Badge>
                      )}
                    </div>
                    <p className="font-semibold text-slate-800 truncate mt-1">{it.title}</p>
                    <p className="text-slate-500">{it.date} · {it.startTime}–{it.endTime} · {it.durH}{t("sched.hrs", "h")}</p>
                    <p className="text-slate-400 flex items-center gap-1"><UserPlus className="h-3 w-3" />{t("tray.needsWorker", "Needs worker")}</p>
                    {it.kind === "job" && (it.requiredWorkerCount || 1) > 1 && (
                      <div className="mt-1"><StaffingBadge assigned={it.workers?.length || 0} required={it.requiredWorkerCount} testid={`tray-staffing-${it.id}`} /></div>
                    )}
                    {types.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`tray-warnchips-${it.id}`}>
                        {types.map((ty) => (
                          <span key={ty} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold flex items-center gap-0.5 ${WARN_CHIP[ty] || "bg-slate-100 text-slate-600"}`}>
                            <AlertTriangle className="h-2.5 w-2.5" />{t(`cal.warn.${ty}`, ty)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
